import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, X, ShoppingCart } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1570821805833-1103c43f9c1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxuYXR1cmUlMjBwYXRoJTIwc3VucmlzZXxlbnwwfHx8fDE3Njk3MjU3Mzh8MA&ixlib=rb-4.1.0&q=85&w=1920";

const DEF_LOGO = "https://customer-assets.emergentagent.com/job_clarity-steps/artifacts/5k4ib0ip_DEF%20.webp";
const INTRO_VIDEO_ID = "v70DvZd9Ytc";
const SHOPIFY_URL = "https://decision-education-store.myshopify.com/products/conversations-for-clarity-instructional-kit";
const C4C_IMAGE = "https://customer-assets.emergentagent.com/job_57c0992f-b7bb-4204-a4d0-363fe8618369/artifacts/4brt8ysj_C4Cblankoutside_247da034-d2d7-4259-aa1e-57062e9bb4d1_1024x1024%402x.webp";

const WelcomeScreen = ({ onStart }) => {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Hero Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/70 via-foreground/50 to-foreground/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="py-6 px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3"
          >
            <img 
              src={DEF_LOGO} 
              alt="Decision Education Foundation" 
              className="h-14 w-auto p-1.5"
            />
          </motion.div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-8">
          <div className="max-w-5xl w-full">
            {/* C4C Framework Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex justify-center mb-8"
            >
              <img
                src={C4C_IMAGE}
                alt="Conversations for Clarity Framework"
                className="max-w-2xl w-full rounded-xl shadow-2xl"
                data-testid="c4c-framework-image"
              />
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center mb-8"
            >
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight tracking-wide">
                Conversations for Clarity
              </h1>
              <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed font-body">
                A proven framework to help you think through important decisions.
                Whether it&apos;s about school, work, or life — find clarity and move forward with confidence.
              </p>
            </motion.div>

            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-10"
            >
              {!showVideo ? (
                <button
                  onClick={() => setShowVideo(true)}
                  data-testid="video-play-button"
                  className="mx-auto block max-w-md w-full group"
                >
                  <div className="relative rounded-lg overflow-hidden shadow-xl">
                    <img
                      src={`https://img.youtube.com/vi/${INTRO_VIDEO_ID}/maxresdefault.jpg`}
                      alt="Watch: What Makes a Good Decision"
                      className="w-full aspect-video object-cover"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${INTRO_VIDEO_ID}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-foreground/30 group-hover:bg-foreground/40 transition-colors">
                      <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-7 h-7 text-primary-foreground ml-1" fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-foreground/90 to-transparent">
                      <p className="text-white text-sm font-medium">Watch: What Makes a Good Decision? (3 min)</p>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="mx-auto max-w-2xl">
                  <div className="relative rounded-lg overflow-hidden shadow-xl">
                    <button
                      onClick={() => setShowVideo(false)}
                      data-testid="video-close-button"
                      className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-foreground/80 flex items-center justify-center text-white hover:bg-foreground transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${INTRO_VIDEO_ID}?autoplay=1`}
                        title="What Makes a Good Decision"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Two CTA Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mb-10"
            >
              {/* CTA 1: Start the Interactive Activity */}
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-xl cursor-pointer group"
                onClick={onStart}
                data-testid="start-survey-card"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <ArrowRight className="w-6 h-6 text-primary" />
                </div>
                <h2 className="font-display text-base text-foreground mb-2">
                  Start the Activity
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed font-body mb-4">
                  Walk through the guided decision-making framework interactively. Takes about 10-15 minutes — no account needed.
                </p>
                <Button
                  onClick={(e) => { e.stopPropagation(); onStart(); }}
                  data-testid="start-survey-button"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-md shadow-md hover:shadow-lg transition-all duration-300 group/btn"
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </motion.div>

              {/* CTA 2: Buy the PDF Instructional Kit */}
              <motion.a
                href={SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl p-6 shadow-xl group block"
                data-testid="buy-pdf-card"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <ShoppingCart className="w-6 h-6 text-primary" />
                </div>
                <h2 className="font-display text-base text-foreground mb-2">
                  Get the Instructional Kit
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed font-body mb-4">
                  Purchase the full Conversations for Clarity instructional kit as a downloadable PDF from the DEF store.
                </p>
                <Button
                  asChild
                  data-testid="buy-pdf-button"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-md shadow-md hover:shadow-lg transition-all duration-300 group/btn"
                >
                  <span>
                    Buy PDF Kit
                    <ShoppingCart className="ml-2 w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                  </span>
                </Button>
              </motion.a>
            </motion.div>

            {/* End of CTAs */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
